import { useEffect, useState } from "react";
import "./ProductReviews.scss";
import {
  projectFirestore,
  timestamp,
  projectStorage,
} from "../../firebase-config/config";
import { useParams } from "react-router-dom";
import { useDocument } from "../../firebase-files/hooks/useDocument";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";
import CustomSnackbar from "../CustomSnackbar";
import { useCollection } from "../../firebase-files/hooks/useCollection";
const ProductReviews = () => {
  const { user } = useAuthContext();
  const { id } = useParams(); // product document id from URL
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]); // for UI preview
  const [loading, setLoading] = useState(false);
  const [reviewsWithUser, setReviewsWithUser] = useState([]);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [userReview, setUserReview] = useState(null);
  const [filterType, setFilterType] = useState("customDefault");

  const { document: review } = useDocument("products", id);
  const { documents: users } = useCollection("users");

  // ✅ Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // ✅ Merge old + new files (without removing existing)
    let combinedFiles = [...files, ...selectedFiles];

    // ✅ Limit to max 5
    if (combinedFiles.length > 5) {
      setSnackbar({
        open: true,
        message: "You can upload a maximum of 5 photos 📸",
        severity: "warning",
      });
      combinedFiles = combinedFiles.slice(0, 5); // keep only first 5
    }

    // ✅ Update files state
    setFiles(combinedFiles);

    // ✅ Generate previews (merge with old)
    const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file));
    const combinedPreviews = [...previewImages, ...newPreviews].slice(0, 5);
    setPreviewImages(combinedPreviews);

    // ✅ Clear the file input value to allow re-selection of same file if needed
    e.target.value = "";
  };

  // ✅ Allow removing a single previewed image
  const handleRemoveImage = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previewImages.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviewImages(updatedPreviews);
  };

  const handleSubmitReview = async () => {
    // if (!rating || !text.trim()) {
    //   setSnackbar({
    //     open: true,
    //     message: "Please provide both rating and review text!",
    //     severity: "warning",
    //   });
    //   return;
    // }

    try {
      setLoading(true);

      // ✅ Upload images to Firebase Storage
      const uploadedURLs = [];
      for (const file of files) {
        const fileRef = projectStorage.ref(
          `product-reviews/${id}/${Date.now()}-${file.name}`
        );
        await fileRef.put(file);
        const url = await fileRef.getDownloadURL();
        uploadedURLs.push(url);
      }

      // ✅ Create review object
      const newReview = {
        uid: user?.phoneNumber || "guest",
        createdAt: timestamp.fromDate(new Date()),
        review: text,
        rating: Number(rating),
        img: uploadedURLs,
      };

      // ✅ Append review manually
      const productRef = projectFirestore.collection("products").doc(id);
      const productSnap = await productRef.get();

      if (!productSnap.exists) {
        throw new Error("Product not found!");
      }

      const data = productSnap.data();
      const existingReviews = Array.isArray(data.reviews) ? data.reviews : [];
      let updatedReviews;

      const existingIndex = existingReviews.findIndex(
        (r) => r.uid === (user?.phoneNumber || "guest")
      );

      if (existingIndex !== -1) {
        // ✅ Update existing review
        updatedReviews = [...existingReviews];
        updatedReviews[existingIndex] = {
          ...existingReviews[existingIndex],
          review: text,
          rating: Number(rating),
          img:
            uploadedURLs.length > 0
              ? uploadedURLs
              : existingReviews[existingIndex].img,
          createdAt: timestamp.fromDate(new Date()),
        };
      } else {
        // ✅ Add new review
        updatedReviews = [...existingReviews, newReview];
      }

      await productRef.update({ reviews: updatedReviews });

      // ✅ Success snackbar
      setSnackbar({
        open: true,
        message: "Review submitted successfully ✅",
        severity: "success",
      });

      // Reset form
      setRating(0);
      setText("");
      setFiles([]);
      setPreviewImages([]);
    } catch (err) {
      console.error("Error adding review:", err);
      setSnackbar({
        open: true,
        message: "Failed to submit review ❌",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!review?.reviews || !Array.isArray(users)) return;

    const mappedReviews = review.reviews.map((r) => {
      const matchedUser = users.find((u) => u.id === r.uid);
      return {
        ...r,
        fullName: matchedUser ? matchedUser.fullName : "Anonymous User",
      };
    });

    // ✅ Move logged-in user's review to the top
    if (user) {
      const currentUserId = user?.phoneNumber || "guest";

      const userReview = mappedReviews.find((r) => r.uid === currentUserId);
      const otherReviews = mappedReviews.filter((r) => r.uid !== currentUserId);

      if (userReview) {
        setReviewsWithUser([userReview, ...otherReviews]);
        return;
      }
    }

    setReviewsWithUser(mappedReviews);
  }, [review, users, user]);

  useEffect(() => {
    if (!user || !reviewsWithUser.length) return;

    const existing = reviewsWithUser.find(
      (r) => r.uid === (user?.phoneNumber || "guest")
    );

    if (existing) {
      setUserReview(existing);
      setRating(existing.rating || 0);
      setText(existing.review || "");
      setPreviewImages(existing.img || []);
      setFiles([]);
    } else {
      setUserReview(null);
      setRating(0);
      setText("");
      setPreviewImages([]);
      setFiles([]);
    }
  }, [reviewsWithUser, user]);

  // ✅ Filter valid ratings (even if review text is empty)
  const validReviews = Array.isArray(review?.reviews)
    ? review.reviews.filter(
        (r) => r && typeof r.rating === "number" && r.rating > 0
      )
    : [];

  const totalRatings = validReviews.length;

  // ✅ Rating counts per star
  const ratingCounts = [5, 4, 3, 2, 1].map(
    (star) => validReviews.filter((r) => Math.round(r.rating) === star).length
  );

  // ✅ Average rating
  const averageRating = totalRatings
    ? (
        validReviews.reduce((sum, r) => sum + Number(r.rating), 0) /
        totalRatings
      ).toFixed(1)
    : 0;

  // ✅ Reviews with actual text
  const totalReviewsWithText = Array.isArray(review?.reviews)
    ? review.reviews.filter((r) => r?.review && r.review.trim().length > 0)
        .length
    : 0;

  // convert Firestore Timestamp or Date to "x time ago" string
  const formatRelativeTime = (tsOrDate) => {
    if (!tsOrDate) return "";

    // If it's a Firestore Timestamp, convert to JS Date
    const date =
      typeof tsOrDate.toDate === "function"
        ? tsOrDate.toDate()
        : new Date(tsOrDate);

    const now = new Date();
    // difference in seconds (integer)
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSeconds < 5) return "just now";
    if (diffSeconds < 60)
      return `${diffSeconds} second${diffSeconds > 1 ? "s" : ""} ago`;

    const units = [
      { name: "year", seconds: 31536000 }, // 365 * 24 * 60 * 60
      { name: "month", seconds: 2592000 }, // 30 * 24 * 60 * 60
      { name: "day", seconds: 86400 }, // 24 * 60 * 60
      { name: "hour", seconds: 3600 },
      { name: "minute", seconds: 60 },
    ];

    for (let i = 0; i < units.length; i++) {
      const { name, seconds } = units[i];
      const value = Math.floor(diffSeconds / seconds);
      if (value >= 1) {
        return `${value} ${name}${value > 1 ? "s" : ""} ago`;
      }
    }

    return "just now";
  };

  // ✅ Get initials (first letter of first + last name)
  const getInitials = (fullName = "") => {
    if (!fullName) return "A"; // default for anonymous

    const nameParts = fullName.trim().split(" ").filter(Boolean);

    if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase(); // single name → first letter
    }

    // first and last
    const first = nameParts[0][0].toUpperCase();
    const last = nameParts[nameParts.length - 1][0].toUpperCase();

    return `${first}${last}`;
  };

  // ✅ Handle Like or Dislike
  const handleReaction = async (reviewIndex, type) => {
    if (!user) {
      setSnackbar({
        open: true,
        message: "Please log in to react to reviews 🔒",
        severity: "warning",
      });
      return;
    }

    const productRef = projectFirestore.collection("products").doc(id);
    const productSnap = await productRef.get();

    if (!productSnap.exists) return;

    const data = productSnap.data();
    const reviews = [...(data.reviews || [])];
    const currentUserId = user?.phoneNumber || "guest";

    const targetReview = reviews[reviewIndex];

    // Initialize likes/dislikes arrays if missing
    targetReview.likes = Array.isArray(targetReview.likes)
      ? targetReview.likes
      : [];
    targetReview.dislikes = Array.isArray(targetReview.dislikes)
      ? targetReview.dislikes
      : [];

    // ✅ Toggle logic
    if (type === "like") {
      if (targetReview.likes.includes(currentUserId)) {
        // remove like
        targetReview.likes = targetReview.likes.filter(
          (u) => u !== currentUserId
        );
      } else {
        targetReview.likes.push(currentUserId);
        // remove dislike if it exists
        targetReview.dislikes = targetReview.dislikes.filter(
          (u) => u !== currentUserId
        );
      }
    } else if (type === "dislike") {
      if (targetReview.dislikes.includes(currentUserId)) {
        // remove dislike
        targetReview.dislikes = targetReview.dislikes.filter(
          (u) => u !== currentUserId
        );
      } else {
        targetReview.dislikes.push(currentUserId);
        // remove like if it exists
        targetReview.likes = targetReview.likes.filter(
          (u) => u !== currentUserId
        );
      }
    }

    await productRef.update({ reviews });

    setSnackbar({
      open: true,
      message:
        type === "like"
          ? "You liked this review 👍"
          : "You disliked this review 👎",
      severity: "success",
    });
  };

  return (
    <div className="product-two-reviews-parent">
      <div className="reviews-header">
        <h3>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24px"
            viewBox="0 -960 960 960"
            width="24px"
            fill="currentColor"
          >
            <path d="M320-160q-33 0-56.5-23.5T240-240v-120h120v-90q-35-2-66.5-15.5T236-506v-44h-46L60-680q36-46 89-65t107-19q27 0 52.5 4t51.5 15v-55h480v520q0 50-35 85t-85 35H320Zm120-200h240v80q0 17 11.5 28.5T720-240q17 0 28.5-11.5T760-280v-440H440v24l240 240v56h-56L510-514l-8 8q-14 14-29.5 25T440-464v104ZM224-630h92v86q12 8 25 11t27 3q23 0 41.5-7t36.5-25l8-8-56-56q-29-29-65-43.5T256-684q-20 0-38 3t-36 9l42 42Zm376 350H320v40h286q-3-9-4.5-19t-1.5-21Zm-280 40v-40 40Z" />
          </svg>{" "}
          Customer Reviews
        </h3>
      </div>

      {/* ⭐ Rating Summary */}
      <div className="rating-summary">
        <div className="rating-overall">
          <h2>
            {averageRating} <span>★</span>
          </h2>
          <p>
            {totalRatings} Ratings & {totalReviewsWithText} Reviews
          </p>
          <p>
            {Array.isArray(review?.reviews) ? review.reviews.length : 0} Total
            Responses
          </p>
        </div>

        <div className="rating-bars">
          {[5, 4, 3, 2, 1].map((star, idx) => {
            const count = ratingCounts[5 - star];
            const percent = totalRatings ? (count / totalRatings) * 100 : 0;
            return (
              <div className="rating-row" key={star}>
                <span className="star-label">{star} ★</span>
                <div className="bar-container">
                  <div
                    className={`bar fill-${star}`}
                    style={{ width: `${percent}%` }}
                  ></div>
                </div>
                <span className="count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* --- Write Review Section --- */}
      {user && user && (
        <>
          <div className="write-review">
            <h4>{userReview ? "Update Your Review" : "Write a Review"}</h4>

            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <span
                  key={s}
                  className={rating >= s ? "active" : ""}
                  onClick={() => setRating(s)}
                >
                  ★
                </span>
              ))}
            </div>

            <div className="feedback-img-review-div">
              <textarea
                placeholder="Share your thoughts..."
                value={text}
                onChange={(e) => setText(e.target.value)}
              />

              <div className="upload-section">
                <label htmlFor="review-images" className="upload-box">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="28"
                    viewBox="0 -960 960 960"
                    width="28"
                    fill="#666"
                  >
                    <path d="M480-440Zm346-240q0-78-54-132t-132-54v-54q100 0 170 70t70 170h-54Zm-106 0q0-33-23.5-56.5T640-760v-54q55 0 93.5 39t40.5 95h-54ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240v80H395l-73 80H160v480h640v-440h80v440q0 33-23.5 56.5T800-120H160Zm320-140q75 0 127.5-52.5T660-440q0-75-52.5-127.5T480-620q-75 0-127.5 52.5T300-440q0 75 52.5 127.5T480-260Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29Z" />
                  </svg>
                  <span>Add Photo/Video</span>
                  <input
                    id="review-images"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </div>
            {/* ✅ Show selected images preview */}
            {files.length > 0 && (
              <p className="upload-count">{files.length}/5 photos selected</p>
            )}

            {previewImages.length > 0 && (
              <div className="preview-images">
                {previewImages.map((img, i) => (
                  <div key={i} className="preview-item">
                    <img src={img} alt={`preview-${i}`} />
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => handleRemoveImage(i)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24px"
                        viewBox="0 -960 960 960"
                        width="24px"
                        fill="currentColor"
                      >
                        <path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              className="btn_fill"
              disabled={loading}
              onClick={handleSubmitReview}
            >
              {loading
                ? "Saving..."
                : userReview
                ? "Update Review"
                : "Submit Review"}
            </button>
          </div>
        </>
      )}

      {/* ✅ Reviews List */}
      <div className="reviews-list">
        {/* --- Filter Section --- */}
        <div className="reviews-filter-bar">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="reviews-filter-dropdown"
          >
            <option value="customDefault">
              ✨ Default (Your Review + Top Liked)
            </option>
            <option value="newest">🕒 Newest First</option>
            <option value="oldest">📅 Oldest First</option>
            <option value="highest">⭐ Highest Rating</option>
            <option value="lowest">🔻 Lowest Rating</option>
            <option value="mostLiked">❤️ Most Liked</option>
          </select>
        </div>
        <div className="reviews-grid">
          {Array.isArray(reviewsWithUser) && reviewsWithUser.length > 0 ? (
            [...reviewsWithUser]
              .sort((a, b) => {
                const currentUserId = user?.phoneNumber || "guest";

                // 👇 Custom default filter (first user's review, then top liked)
                if (filterType === "customDefault") {
                  // 1️⃣ User's own review should always be first
                  if (a.uid === currentUserId) return -1;
                  if (b.uid === currentUserId) return 1;

                  // 2️⃣ After that, sort by likes count (most liked first)
                  return (b.likes?.length || 0) - (a.likes?.length || 0);
                }

                // 👇 Other filters as before
                switch (filterType) {
                  case "newest":
                    return (
                      new Date(b.createdAt?.toDate?.() || b.createdAt) -
                      new Date(a.createdAt?.toDate?.() || a.createdAt)
                    );
                  case "oldest":
                    return (
                      new Date(a.createdAt?.toDate?.() || a.createdAt) -
                      new Date(b.createdAt?.toDate?.() || b.createdAt)
                    );
                  case "highest":
                    return b.rating - a.rating;
                  case "lowest":
                    return a.rating - b.rating;
                  case "mostLiked":
                    return (b.likes?.length || 0) - (a.likes?.length || 0);
                  default:
                    return 0;
                }
              })

              .map((r, i) => (
                <div className="review-card" key={i}>
                  <div className="review-top">
                    <div className="review-info">
                      <div className="profile-with-latters">
                        {getInitials(r.fullName || "Anonymous")}
                      </div>
                      <div>
                        <p className="reviewer-name">
                          {r.fullName || "Anonymous User"}
                        </p>
                        <p className="review-time">
                          {formatRelativeTime(r.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="stars">
                      {"★".repeat(Math.round(r.rating || 0))}
                      {"☆".repeat(5 - Math.round(r.rating || 0))}
                    </div>
                  </div>

                  <p className="review-text">{String(r.review || "")}</p>

                  {Array.isArray(r.img) && r.img.length > 0 && (
                    <div className="review-images">
                      {r.img.map((img, j) => (
                        <img
                          src={img}
                          alt={`Review ${j}`}
                          key={j}
                          onClick={() => setZoomedImage(img)} // 👈 open zoom
                        />
                      ))}
                    </div>
                  )}

                  <div className="review-reactions">
                    <button
                      className={`reaction-btn ${
                        r.likes?.includes(user?.phoneNumber || "guest")
                          ? "active-like"
                          : ""
                      }`}
                      onClick={() => handleReaction(i, "like")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="currentColor"
                      >
                        <path d="M720-120H280v-520l280-280 50 50q7 7 11.5 19t4.5 23v14l-44 174h258q32 0 56 24t24 56v80q0 7-2 15t-4 15L794-168q-9 20-30 34t-44 14Z" />
                      </svg>
                      <span>{r.likes?.length || 0}</span>
                    </button>

                    <button
                      className={`reaction-btn ${
                        r.dislikes?.includes(user?.phoneNumber || "guest")
                          ? "active-dislike"
                          : ""
                      }`}
                      onClick={() => handleReaction(i, "dislike")}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="currentColor"
                      >
                        <path d="M240-840h440v520L400-40l-50-50q-7-7-11.5-19t-4.5-23v-14l44-174H120q-32 0-56-24t-24-56v-80q0-7 2-15t4-15l120-282q9-20 30-34t44-14Z" />
                      </svg>
                      <span>{r.dislikes?.length || 0}</span>
                    </button>
                  </div>
                </div>
              ))
          ) : (
            <p>No reviews yet</p>
          )}
        </div>

        {zoomedImage && (
          <div className="zoom-overlay" onClick={() => setZoomedImage(null)}>
            <div className="zoom-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="zoom-close"
                onClick={() => setZoomedImage(null)}
              >
                ✕
              </button>
              <img src={zoomedImage} alt="Zoomed" className="zoomed-img" />
            </div>
          </div>
        )}
      </div>

      {/* ✅ Snackbar Component */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </div>
  );
};

export default ProductReviews;
